import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { isAdminAuthenticated, logoutAdmin } from "@/data/adminAuthStore";
import { api } from "@/services/api";

/**
 * Guarda las rutas del portal administrativo.
 *
 * Contrato fail-closed:
 * 1. Sin snapshot local (`ct_admin_session_v1`) → redirect inmediato.
 * 2. Con snapshot → estado `checking`: se muestra un placeholder
 *    neutro (sin sidebar/dashboard) mientras `/api/auth/me` responde.
 * 3. `/me` 200 → `allowed`, se renderiza el shell admin.
 * 4. `/me` 401/403 → snapshot forjado o cookie expirada: se limpia y
 *    redirige a `/admin/login`.
 * 5. Falla de red → NO conceder acceso: fail-closed. Se limpia snapshot
 *    y redirige (el usuario reintenta login).
 */
export default function AdminProtectedRoute() {
  const location = useLocation();
  const [state, setState] = useState(
    isAdminAuthenticated() ? "checking" : "denied",
  );

  useEffect(() => {
    let cancelled = false;
    if (state !== "checking") return;
    (async () => {
      try {
        await api.get("/auth/me");
        if (!cancelled) setState("allowed");
      } catch (err) {
        // Cualquier fallo (401/403/network) → fail-closed.
        logoutAdmin();
        if (!cancelled) setState("denied");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state]);

  if (state === "denied") {
    return (
      <Navigate
        to="/admin/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }
  if (state === "checking") {
    // Placeholder neutro: NUNCA revela sidebar/dashboard hasta que el
    // backend confirme la sesión.
    return (
      <div
        data-testid="admin-guard-checking"
        className="min-h-[100dvh] grid place-items-center bg-background"
      >
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          <span className="text-sm">Verificando sesión…</span>
        </div>
      </div>
    );
  }
  return <Outlet />;
}
